import { Drawer, Typography, Alert, Flex } from '@arkestro/arkestro-design-system';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useContext, useState } from 'react';
import { UploadFile } from 'antd'; // UploadFile is not currently exported by ADS
import { DownloadOutlined } from '@ant-design/icons';
import Trans from 'next-translate/Trans';
import { Footer } from '@components/RfxDrawers/shared';
import { createEventFromFile } from '@components/bid_request/eventCreationFileActions';
import { exportExcelSheet } from '@components/bid_request/details/utils/export/exportExcelSheet';
import { getSampleExportLotsData } from '@components/bid_request/details/utils/export/getSampleExportLotsData';
import { UserContext } from '@components/providers/UserProvider/UserProvider';
import { EventRequest, User } from 'types';
import { defaultLineItemsState } from '@components/providers/LineItemsProvider';
import XlsxImg from '@shared/file-icons/xlsx.svg';
import { DraggerPanel } from './DraggerPanel';
import styles from './RffDrawer.module.scss';
import sharedStyles from '@components/RfxDrawers/shared/SharedStyles.module.scss';

interface RffDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RffDrawer = ({ isOpen, onClose }: RffDrawerProps) => {
  const { t } = useTranslation('common');
  const user = useContext(UserContext) as unknown as User;

  // In theory this list should only ever be 1 file long
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const uploadCompleted = fileList.length > 0 && fileList[0].status === 'done';

  const {
    event: {
      showPricingColumns,
      setBaselineAverage,
      showDiscount,
      showLogisticsCharge
    },
    lots,
  } = defaultLineItemsState;

  const handleFileChange = (info: unknown) => {
    // Get the most recently added file from the file list
    // Clearing the react state doesn't actually clear the file list in the antd component
    // so we need to get the last file from the list to ensure we have the correct file to upload
    const newFileList = (info as { fileList: UploadFile[] }).fileList.slice(-1);

    setFileList(newFileList);
  };

  const onSubmit = () => {
    if (fileList.length > 0) {
      createEventFromFile(fileList[0].originFileObj as File, t);
      setFileList([]);
      onClose();
    }
  };

  const handleOnClose = () => {
    setFileList([]);
    onClose();
  };

  const handleDownloadSample = useCallback(() => {
    exportExcelSheet({
      isSampleDownload: true,
      lots: getSampleExportLotsData(lots),
      request: {} as EventRequest,
      setBaselineFromQuotes: setBaselineAverage,
      showDiscount,
      showLogisticsCharge,
      showPricePerUnit: showPricingColumns,
      t,
      user
    });
  }, [
    lots,
    setBaselineAverage,
    showDiscount,
    showLogisticsCharge,
    showPricingColumns,
    t,
    user,
  ]);

  return (
    <Drawer
      classNames={{ body: sharedStyles.drawerBody, footer: sharedStyles.drawerFooter, header: sharedStyles.drawerHeader }}
      footer={<Footer isSaving={false} isValid={uploadCompleted} onClose={handleOnClose} onSubmit={onSubmit} submitLabel={t('rfx_drawer.rff.submit_button')} type='RFF' />}
      loading={false}
      onClose={handleOnClose}
      open={isOpen}
      title={t('rfx_drawer.rff.title')}
      width={680}
    >
      <Flex className={styles.uploadPanel} gap={16} vertical>
        <Flex gap={4} vertical>
          <Typography className={styles.uploadHeader}>
            {t('rfx_drawer.rff.upload_file')}
          </Typography>
          <Typography className={styles.text}>
            {t('rfx_drawer.rff.upload_file_subheader')}
          </Typography>
        </Flex>

        <DraggerPanel fileList={fileList} handleFileChange={handleFileChange} />

        <Flex align='center' className={styles.sampleFileCard} data-testid='download-sample' justify='space-between'>
          <Flex align='center' gap={4} justify='center'>
            <img alt='file upload' height={45} src={XlsxImg.src} width={45} />
            <Flex vertical>
              <Typography className={styles.templateHeader}>
                {t('rfx_drawer.rff.sample_file')}
              </Typography>
              <Typography className={styles.templateSubHeader}>
                {t('rfx_drawer.rff.sample_file_name')}
              </Typography>
            </Flex>
          </Flex>
          <Flex align='center' className={styles.downloadSample} gap={4} justify='center' onClick={handleDownloadSample}>
            <DownloadOutlined className={styles.sampleFileIcon} />
            <Typography className={styles.sampleFileText}>
              {t('rfx_drawer.rff.download_sample_file')}
            </Typography>
          </Flex>
        </Flex>

        <Flex gap={4} vertical>
          <Typography className={styles.requirementsHeader}>{t('rfx_drawer.rff.requirements_header')}</Typography>
          <ul>
            <li>
              <Typography className={styles.requirementsText}><Trans components={{ strong: <strong /> }} i18nKey="common:rfx_drawer.rff.requirements_line_1" /></Typography>
            </li>
            <li>
              <Typography className={styles.requirementsText}>{t('rfx_drawer.rff.requirements_line_2')}</Typography>
            </li>
            <li>
              <Typography className={styles.requirementsText}>{t('rfx_drawer.rff.requirements_line_3')}</Typography>
            </li>
          </ul>
        </Flex>

        <Alert
          closable={false}
          message={t('rfx_drawer.rff.pdf_warning')}
          showIcon
          type='warning'
        />
      </Flex>
    </Drawer>
  );
};
